import {
  OrgFeatureKey,
  isOrgFeatureEnabled,
  withOrgFeature,
} from "@/src/constants/org-features";
import { ORGANIZATION_CONFIG_QUERY_KEYS } from "@/src/query-keys/organization.keys";
import { decodeToken } from "@/src/services/api/auth.api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import gqlClient from "../api/graphql.client";
import {
  GET_ORG_FEATURES,
  INSERT_ORG_FEATURES,
  UPDATE_ORG_FEATURES,
} from "./org-features.query";

type OrgFeaturesRow = Readonly<{
  OrganizationId: string;
  enabledFeatures?: string[] | null;
}>;

type OrgFeaturesState = Readonly<{
  /** The org's config row, or null when it has none yet. */
  row: OrgFeaturesRow | null;
  enabledFeatures: string[];
}>;

const NONE: OrgFeaturesState = { row: null, enabledFeatures: [] };

const useOrgFeaturesQuery = () =>
  useQuery<OrgFeaturesState>({
    queryKey: ORGANIZATION_CONFIG_QUERY_KEYS.features,
    queryFn: async () => {
      const decoded = await decodeToken();
      const client = await gqlClient();
      const data = await client.request<{
        getorganizationconfigs: { items: OrgFeaturesRow[] } | null;
      }>(GET_ORG_FEATURES, {
        where: { OrganizationId: { eq: decoded?.org_id } },
      });
      const row = data.getorganizationconfigs?.items?.[0] ?? null;
      return { row, enabledFeatures: row?.enabledFeatures ?? [] };
    },
    staleTime: 60_000,
    // A failed read must not lock anyone out of the app, only keep the optional
    // features hidden, so don't hammer a project whose schema lacks the field.
    retry: false,
  });

/**
 * Whether an org-level feature is switched on for the signed-in user's org.
 * Everything reads as off while the read is pending, so a feature never
 * flashes on; an optional feature is never exposed by an error.
 */
export const useOrgFeature = (key: OrgFeatureKey) => {
  const { data, isLoading } = useOrgFeaturesQuery();
  // Nothing is shown while loading. A failed read falls back to the defaults:
  // optional features stay hidden, default-on ones (leaderboard) stay visible.
  return {
    enabled: !isLoading && isOrgFeatureEnabled(data?.enabledFeatures, key),
    isLoading,
  };
};

/** Every feature's state plus the toggle, for the admin settings tab. */
export const useOrgFeatures = () => {
  const queryClient = useQueryClient();
  const { data = NONE, isLoading, isError } = useOrgFeaturesQuery();

  const {
    mutateAsync: setFeature,
    isPending,
    variables,
  } = useMutation({
    mutationFn: async ({ key, on }: { key: OrgFeatureKey; on: boolean }) => {
      const decoded = await decodeToken();
      const client = await gqlClient();
      // Re-read the row so two admins toggling different features don't
      // overwrite each other with a stale list.
      const fresh = await client.request<{
        getorganizationconfigs: { items: OrgFeaturesRow[] } | null;
      }>(GET_ORG_FEATURES, {
        where: { OrganizationId: { eq: decoded?.org_id } },
      });
      const row = fresh.getorganizationconfigs?.items?.[0] ?? null;
      const enabledFeatures = withOrgFeature(row?.enabledFeatures, key, on);

      // A missing row must be created: an update would match nothing and
      // silently drop the change.
      if (!row) {
        await client.request(INSERT_ORG_FEATURES, { enabledFeatures });
      } else {
        await client.request(UPDATE_ORG_FEATURES, {
          organizationId: decoded?.org_id,
          enabledFeatures,
        });
      }
      return enabledFeatures;
    },
    onSuccess: (enabledFeatures) => {
      queryClient.setQueryData(ORGANIZATION_CONFIG_QUERY_KEYS.features, {
        row: data.row ?? { OrganizationId: "" },
        enabledFeatures,
      });
      queryClient.invalidateQueries({
        queryKey: ORGANIZATION_CONFIG_QUERY_KEYS.features,
      });
    },
  });

  return {
    enabledFeatures: data.enabledFeatures,
    isLoading,
    isError,
    isEnabled: (key: OrgFeatureKey) =>
      isOrgFeatureEnabled(data.enabledFeatures, key),
    setFeature,
    isSaving: isPending,
    savingKey: isPending ? variables?.key : undefined,
  };
};
